/* ============================================================================
   BbLAB content pages — progressive enhancement only.
   Story timeline tabs (keyboard), Business audience tabs + native form error
   styling + "at least one" validation for required checkbox groups, and a
   small Search input enhancement. No submit is ever faked: the Business page
   posts the real Shopify {% form 'contact' %} and only the server decides
   posted_successfully? / errors. Checkbox options post under independent
   per-option contact[...] keys, so the native form never drops a selection;
   the group check below is a JS enhancement scoped to the form being
   submitted (the active audience), never to every option as `required`.
   ============================================================================ */
(function () {
  'use strict';

  // Calls fn(node, index) for every node. `nodes` is any array-like (NodeList).
  function each(nodes, fn) {
    for (var i = 0; i < nodes.length; i += 1) fn(nodes[i], i);
  }

  // Each root initialises once. Shopify re-executes this asset (duplicate
  // <script> tags, theme-editor section reloads), so idempotency lives on the
  // DOM node itself: a fresh script execution would not share closure state.
  var INIT_FLAG = '__bblabContentInit';

  function isInitialised(node) {
    return node[INIT_FLAG] === true;
  }

  function markInitialised(node) {
    node[INIT_FLAG] = true;
  }

  // querySelectorAll from a scope, including the scope itself when it matches
  // (shopify:section:load can target the section element directly).
  function queryAll(scope, selector) {
    var found = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector) && found.indexOf(scope) === -1) found.unshift(scope);
    return found;
  }

  /* ---- Story: timeline tablist with Arrow/Home/End keyboard support ------
     Switching slides the outgoing and incoming year panels in parallel inside
     the clipped viewport (~0.6s, handled by CSS). The leaving video is paused
     the moment the switch starts — never after `hidden` — and a rapid second
     click discards the pending animation and lands on the final target only.
     With prefers-reduced-motion (or no requestAnimationFrame) the switch is an
     instant `hidden` toggle, which is also the legacy behaviour. ------------- */
  var STORY_ENTER = ['is-entering-from-right', 'is-entering-from-left'];
  var STORY_LEAVE = ['is-leaving-to-left', 'is-leaving-to-right'];

  // The media layer never guesses which film is active: after every select path
  // has set the panel active state (including re-selecting the already active
  // tab) the controller emits one bubbling event on the selected panel. The
  // media script listens for it on the timeline and restarts only that film.
  var STORY_SELECT_EVENT = 'bblab:story-panel-select';

  function emitStorySelection(panel, index) {
    if (!panel || typeof panel.dispatchEvent !== 'function') return;
    var detail = { panel: panel, index: index };
    var event = null;
    if (typeof CustomEvent === 'function') {
      event = new CustomEvent(STORY_SELECT_EVENT, { bubbles: true, cancelable: false, detail: detail });
    } else if (document.createEvent) {
      event = document.createEvent('CustomEvent');
      if (event && event.initCustomEvent) event.initCustomEvent(STORY_SELECT_EVENT, true, false, detail);
      else event = null;
    }
    if (!event) return;
    // A hostile/minimal DOM must still complete the selection itself.
    try { panel.dispatchEvent(event); } catch (error) { /* selection is never blocked by the announcement */ }
  }

  function initStoryTimelines(scope) {
    each(queryAll(scope || document, '[data-bblab-story-timeline]'), function (timeline) {
      if (isInitialised(timeline)) return;
      var tabs = Array.prototype.slice.call(timeline.querySelectorAll('[role="tab"]'));
      var panels = Array.prototype.slice.call(timeline.querySelectorAll('[data-story-panel]'));
      if (!tabs.length || !panels.length) return;

      var selected = tabs.findIndex(function (tab) { return tab.getAttribute('aria-selected') === 'true'; });
      var current = selected < 0 ? 0 : selected;
      var timer = null;
      var rafId = null;
      // Bumped on every switch: a pending rAF/close timer from an older switch
      // is ignored so rapid clicks land on the final target only.
      var generation = 0;
      var viewport = timeline.querySelector('[data-story-viewport]');

      function reducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      }
      function canAnimate() {
        return !reducedMotion() && typeof window.requestAnimationFrame === 'function';
      }
      function clearClasses(panel) {
        STORY_ENTER.concat(STORY_LEAVE).forEach(function (name) { panel.classList.remove(name); });
      }
      function setPanelState(panel, inactive) {
        panel.setAttribute('aria-hidden', inactive ? 'true' : 'false');
        if (inactive) panel.setAttribute('inert', '');
        else panel.removeAttribute('inert');
      }
      function pauseVideos(panel) {
        each(panel.querySelectorAll('video'), function (video) {
          if (!video.paused) video.pause();
        });
      }
      function setTabs(index, focus) {
        tabs.forEach(function (tab, i) {
          var active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });
        if (focus) tabs[index].focus();
      }
      // Panels can have different media heights; freeze the viewport at the
      // outgoing height for the duration of the slide so the page never jumps.
      function lockViewportHeight(panel) {
        if (!viewport || !panel) return;
        var height = panel.offsetHeight;
        if (!height && panel.getBoundingClientRect) height = panel.getBoundingClientRect().height;
        if (height) viewport.style.height = height + 'px';
      }
      function releaseViewportHeight() {
        if (viewport) viewport.style.height = '';
      }

      function select(index, focus) {
        var next = Math.min(Math.max(index, 0), tabs.length - 1);
        var previous = current;
        if (timer && window.clearTimeout) { window.clearTimeout(timer); timer = null; }
        if (rafId && window.cancelAnimationFrame) { window.cancelAnimationFrame(rafId); rafId = null; }
        var gen = ++generation;
        setTabs(next, focus);
        // The leaving film stops immediately, before it is ever hidden.
        panels.forEach(function (panel, i) { if (i !== next) pauseVideos(panel); });

        if (!canAnimate() || previous === next) {
          panels.forEach(function (panel, i) {
            panel.hidden = i !== next;
            clearClasses(panel);
            setPanelState(panel, i !== next);
          });
          releaseViewportHeight();
          current = next;
          emitStorySelection(panels[next], next);
          return;
        }

        var forward = next > previous;
        panels.forEach(function (panel, i) {
          if (i === next || i === previous) return;
          panel.hidden = true;
          clearClasses(panel);
          setPanelState(panel, true);
        });
        var incoming = panels[next];
        var outgoing = panels[previous];
        lockViewportHeight(outgoing || incoming);
        incoming.hidden = false;
        setPanelState(incoming, false);
        clearClasses(incoming);
        incoming.classList.add(forward ? 'is-entering-from-right' : 'is-entering-from-left');
        if (outgoing && outgoing !== incoming) {
          outgoing.hidden = false;
          setPanelState(outgoing, true);
          clearClasses(outgoing);
          outgoing.classList.add(forward ? 'is-leaving-to-left' : 'is-leaving-to-right');
        }
        current = next;
        // Announce the selection only once the incoming panel is the active
        // one; the outgoing panel already carries aria-hidden/inert.
        emitStorySelection(incoming, next);
        // Flush the start transform, then release it on the next frame so the
        // browser cannot merge the two styles into one (which skipped the
        // incoming animation entirely).
        if (typeof incoming.offsetWidth === 'number') void incoming.offsetWidth;
        rafId = window.requestAnimationFrame(function () {
          rafId = window.requestAnimationFrame(function () {
            rafId = null;
            if (gen !== generation) return;
            incoming.classList.remove('is-entering-from-right', 'is-entering-from-left');
          });
        });
        timer = window.setTimeout(function () {
          timer = null;
          if (gen !== generation) return;
          panels.forEach(function (panel, i) {
            if (i === next) return;
            panel.hidden = true;
            clearClasses(panel);
            setPanelState(panel, true);
          });
          clearClasses(incoming);
          releaseViewportHeight();
        }, 620);
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(i, false); });
        tab.addEventListener('keydown', function (event) {
          var key = event.key;
          var at = tabs.indexOf(event.currentTarget);
          if (key === 'ArrowRight' || key === 'ArrowDown') { event.preventDefault(); select(at + 1, true); }
          else if (key === 'ArrowLeft' || key === 'ArrowUp') { event.preventDefault(); select(at - 1, true); }
          else if (key === 'Home') { event.preventDefault(); select(0, true); }
          else if (key === 'End') { event.preventDefault(); select(tabs.length - 1, true); }
        });
      });

      select(current, false);
      markInitialised(timeline);
    });
  }

  /* ---- Business: audience tabs. Both native contact forms stay in the DOM,
     so typed values survive switching; only visibility changes. ------------- */
  function initBusinessTabs(scope) {
    each(queryAll(scope || document, '[data-bblab-business]'), function (business) {
      if (isInitialised(business)) return;
      var tabs = Array.prototype.slice.call(business.querySelectorAll('[data-audience]'));
      var panels = Array.prototype.slice.call(business.querySelectorAll('[data-audience-panel]'));
      if (!tabs.length || !panels.length) return;

      function show(audience, focus) {
        cancelPendingFocus();
        tabs.forEach(function (tab) {
          var active = tab.dataset.audience === audience;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        panels.forEach(function (panel) { panel.hidden = panel.dataset.audiencePanel !== audience; });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { show(tab.dataset.audience, false); });
        tab.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          var i = tabs.indexOf(event.currentTarget);
          var next = (i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          show(tabs[next].dataset.audience, true);
        });
      });

      initialiseFormErrors(business);
      initialiseRequiredGroups(business);
      var selected = tabs.filter(function (tab) { return tab.getAttribute('aria-selected') === 'true'; })[0];
      var initial = selected ? selected.dataset.audience : tabs[0].dataset.audience;
      /* Phone-only initial audience: the final 750 mobile PSD shows the
         individual form by default, while the desktop default stays exactly
         what the Liquid rendered. Reading the data attribute here (scoped to
         <=760px) means a user click on 法人 / 個人 afterwards still switches
         normally through show(). */
      var mobileDefault = business.getAttribute('data-bblab-business-mobile-default');
      if (mobileDefault && window.matchMedia && window.matchMedia('(max-width: 760px)').matches) {
        var hasMobileDefault = tabs.some(function (tab) { return tab.dataset.audience === mobileDefault; });
        if (hasMobileDefault) initial = mobileDefault;
      }
      show(initial, false);
      markInitialised(business);
    });
  }

  /* ---- Business: a required checkbox group means "at least one", not
     "every option"; only the form being submitted is validated. ------------- */
  function initialiseRequiredGroups(scope) {
    each(scope.querySelectorAll('form'), function (form) {
      form.addEventListener('submit', function (event) {
        if (validateRequiredGroups(form)) return;
        event.preventDefault();
      });
    });
    each(scope.querySelectorAll('[data-biz-checkbox-group]'), function (group) {
      each(group.querySelectorAll('input[type="checkbox"]'), function (box) {
        box.addEventListener('change', function () {
          if (group.getAttribute('data-required') !== 'true') return;
          if (hasChecked(group)) clearGroupError(group);
        });
      });
    });
  }

  function hasChecked(group) {
    return Array.prototype.slice.call(group.querySelectorAll('input[type="checkbox"]'))
      .some(function (box) { return box.checked; });
  }

  function validateRequiredGroups(form) {
    var valid = true;
    each(form.querySelectorAll('[data-biz-checkbox-group]'), function (group) {
      if (group.getAttribute('data-required') !== 'true') return;
      if (hasChecked(group)) {
        clearGroupError(group);
      } else {
        markGroupError(group);
        valid = false;
      }
    });
    return valid;
  }

  function groupErrorId(group) {
    var id = group.getAttribute('data-biz-error-id');
    if (!id) {
      ERROR_SEQ += 1;
      id = 'bblab-group-error-' + ERROR_SEQ;
      group.setAttribute('data-biz-error-id', id);
    }
    return id;
  }

  // Appends our id, keeping every pre-existing/external description id.
  function describe(control, id) {
    var current = control.getAttribute('aria-describedby');
    var ids = current ? current.split(/\s+/).filter(Boolean) : [];
    if (ids.indexOf(id) === -1) ids.push(id);
    control.setAttribute('aria-describedby', ids.join(' '));
    control.setAttribute('aria-invalid', 'true');
  }

  function undescribe(control, id) {
    var current = control.getAttribute('aria-describedby');
    if (current) {
      var ids = current.split(/\s+/).filter(Boolean).filter(function (value) { return value !== id; });
      if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
      else control.removeAttribute('aria-describedby');
    }
    control.removeAttribute('aria-invalid');
  }

  function clearGroupError(group) {
    group.classList.remove('field-invalid');
    var note = group.querySelector('.field-error');
    if (note) note.remove();
    var id = group.getAttribute('data-biz-error-id');
    if (!id) return;
    var current = group.getAttribute('aria-describedby');
    if (current) {
      var ids = current.split(/\s+/).filter(Boolean).filter(function (value) { return value !== id; });
      if (ids.length) group.setAttribute('aria-describedby', ids.join(' '));
      else group.removeAttribute('aria-describedby');
    }
    group.removeAttribute('aria-invalid');
  }

  function markGroupError(group) {
    group.classList.add('field-invalid');
    var id = groupErrorId(group);
    if (!group.querySelector('.field-error')) {
      var note = document.createElement('small');
      note.className = 'field-error';
      note.setAttribute('role', 'alert');
      note.setAttribute('id', id);
      note.textContent = group.getAttribute('data-error-message') || '1つ以上選択してください。';
      group.append(note);
    }
    describe(group, id);
  }

  /* ---- Business: fixed Japanese notes + stable id/ARIA; focus only from an
     explicit submit intent, to the FIRST invalid control. ------------------- */
  var ERROR_TEXT = {
    required: 'この項目は必須です。',
    select: '選択してください。',
    consent: '同意が必要です。',
    email: 'メールアドレスの形式が正しくありません。',
    generic: '入力内容をご確認ください。',
  };
  var ERROR_SUFFIX = '-error';
  var ERROR_SEQ = 0;
  var pendingFocus = null;

  function errorMessage(control) {
    var validity = control.validity || {};
    if (validity.valueMissing) {
      if (control.tagName === 'SELECT') return ERROR_TEXT.select;
      if (control.type === 'checkbox') return ERROR_TEXT.consent;
      return ERROR_TEXT.required;
    }
    if (control.type === 'email' && (validity.typeMismatch || validity.badInput)) return ERROR_TEXT.email;
    return ERROR_TEXT.generic;
  }

  function errorId(control) {
    if (control.id) return control.id + ERROR_SUFFIX;
    var stored = control.getAttribute('data-biz-error-id');
    if (!stored) {
      ERROR_SEQ += 1;
      stored = 'bblab-field-error-' + ERROR_SEQ;
      control.setAttribute('data-biz-error-id', stored);
    }
    return stored;
  }

  function fieldHolder(control) {
    return control.closest('.field') || control.closest('fieldset') || control.parentElement;
  }

  function markControl(control) {
    var holder = fieldHolder(control);
    if (!holder) return;
    var id = errorId(control);
    var note = holder.querySelector('.field-error');
    if (!note) {
      note = document.createElement('small');
      note.className = 'field-error';
      note.setAttribute('role', 'alert');
      // Consent label is two-column (checkbox + text span): nest the note
      // under the text, never as a third flex column. Safe fallback.
      var consent = control.closest ? control.closest('label.consent') : null;
      (consent && consent.querySelector('span') || holder).append(note);
    }
    note.setAttribute('id', id);
    note.textContent = errorMessage(control);
    holder.classList.add('field-invalid');
    describe(control, id);
  }

  function clearControl(control) {
    var holder = fieldHolder(control);
    var id = errorId(control);
    if (holder) {
      var note = holder.querySelector('.field-error');
      if (note && note.getAttribute('id') === id) note.remove();
      if (!holder.querySelector('.field-error')) holder.classList.remove('field-invalid');
    }
    undescribe(control, id);
  }

  // First invalid interactive field in DOM order; validity is read, not re-run.
  function firstErrorControl(form) {
    var controls = Array.prototype.slice.call(form.querySelectorAll('input, select, textarea'));
    for (var i = 0; i < controls.length; i += 1) {
      if (controls[i].disabled) continue;
      if (controls[i].validity && controls[i].validity.valid === false) return controls[i];
    }
    var groups = Array.prototype.slice.call(form.querySelectorAll('[data-biz-checkbox-group]'));
    for (var j = 0; j < groups.length; j += 1) {
      if (groups[j].getAttribute('data-required') !== 'true' || hasChecked(groups[j])) continue;
      var box = groups[j].querySelector('input[type="checkbox"]');
      if (box && !box.disabled) return box;
    }
    return null;
  }

  function cancelPendingFocus() {
    if (pendingFocus && window.clearTimeout) window.clearTimeout(pendingFocus.timer);
    pendingFocus = null;
  }

  function focusFirstError(form) {
    if (!form || form.hidden) return;
    var panel = form.closest && form.closest('[data-audience-panel]');
    if (panel && panel.hidden) return;
    var control = firstErrorControl(form);
    if (!control || control.isConnected === false) return;
    control.focus();
    if (typeof control.scrollIntoView === 'function') control.scrollIntoView({ block: 'center' });
  }

  // One bounded pending focus; later intents/audience switches supersede it.
  function scheduleFirstErrorFocus(form) {
    cancelPendingFocus();
    if (!window.setTimeout) return;
    var entry = { form: form, timer: null };
    pendingFocus = entry;
    entry.timer = window.setTimeout(function () {
      if (pendingFocus !== entry) return;
      pendingFocus = null;
      focusFirstError(form);
    }, 0);
  }

  var ENTER_TYPES = ['text', 'email', 'tel', 'search', 'url', 'password', 'number'];

  function submitIntent(form) {
    form.addEventListener('click', function (event) {
      var target = event.target;
      var submitter = target && target.closest ? target.closest('[type="submit"]') : null;
      if (submitter && form.contains && form.contains(submitter)) scheduleFirstErrorFocus(form);
    });
    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var target = event.target;
      if (!target || target.tagName !== 'INPUT' || ENTER_TYPES.indexOf(target.type) === -1) return;
      scheduleFirstErrorFocus(form);
    });
  }

  function initialiseFormErrors(scope) {
    each(scope.querySelectorAll('form'), function (form) {
      each(form.querySelectorAll('input, select, textarea'), function (control) {
        if (control.closest('[data-biz-checkbox-group]')) return;
        control.addEventListener('invalid', function (event) { event.preventDefault(); markControl(control); });
        control.addEventListener('input', function () { clearControl(control); });
        control.addEventListener('change', function () { clearControl(control); });
      });
      submitIntent(form);
    });
  }

  /* ---- Search: keep the native GET form, just trim empty queries --------- */
  function initSearch(scope) {
    each(queryAll(scope || document, '[data-bblab-search]'), function (search) {
      if (isInitialised(search)) return;
      var form = search.querySelector('form[role="search"]');
      if (!form) return;
      form.addEventListener('submit', function (event) {
        var input = form.querySelector('input[name="q"]');
        if (input && !input.value.trim()) event.preventDefault();
      });
      markInitialised(search);
    });
  }

  /* ---- Business: 提交不能静默失败 --------------------------------------------
     Shopify 的 captcha-bootstrap 在「表单受保护但挑战尚未绑定」时会 preventDefault，
     如果 hCaptcha 的提供方脚本没加载成功（网络被拦、扩展、风控故障），点击提交会
     完全没反应：不跳转、不发请求、也没有任何提示。这里兜住这种情况，给用户可见的
     失败提示和直接联系方式。成功提交会跳转，上下文销毁，定时器自然失效。 -------- */
  var SUBMIT_WATCHDOG_MS = 7000;
  var CAPTCHA_PENDING_MAX_MS = 60000;

  /* 验证码已绑定但挑战还没完成时，不能算「提交失败」：用户可能正在勾选。
     只有拿到令牌、或者根本没有绑定验证码，才在超时后报失败。 */
  function captchaPending(form) {
    if (!(form.dataset.hcaptchaBound || form.dataset.recaptchaBound)) return false;
    var token = form.querySelector(
      'textarea[name="h-captcha-response"], input[name="h-captcha-response"],' +
      'textarea[name="g-recaptcha-response"], input[name="g-recaptcha-response"],' +
      'input[name="recaptcha-v3-token"]'
    );
    return !token || !String(token.value || '').trim();
  }

  function showSubmitFailure(form) {
    var box = form.querySelector('.form-status');
    if (!box) return;
    var text = form.getAttribute('data-submit-error');
    if (!text) return;
    box.classList.add('is-error');
    box.setAttribute('role', 'alert');
    box.textContent = text;
  }

  /* Shopify 的 captcha-bootstrap 会在 DOMContentLoaded 给受保护的联系表单绑定
     hCaptcha。本页实测没有绑定（供应商脚本始终没有被拉起），结果是提交被
     preventDefault 后既不跳转也不发请求。这里对「受保护但未绑定」的表单补一次绑定；
     失败就静默交给上面的看门狗兜底。只调用 Shopify 自己的公开挂载点，不伪造令牌。 */
  function bindStorefrontCaptcha(form) {
    try {
      var cap = window.Shopify && window.Shopify.captcha;
      if (!cap || typeof cap.protect !== 'function') return;
      if (form.dataset.hcaptchaBound || form.dataset.recaptchaBound || form.dataset.cptcha) return;
      if (!form.querySelector('input[name="form_type"][value="contact"]')) return;
      if (form.getAttribute('data-nocaptcha') === 'true') return;
      cap.protect(form, false);
    } catch (error) {
      /* 验证码不可用时仍然要能提示用户，不在这里报错。 */
    }
  }

  function initSubmitWatchdog(scope) {
    var schedule = (typeof setTimeout === 'function') ? setTimeout : (window.setTimeout || null);
    var cancel = (typeof clearTimeout === 'function') ? clearTimeout : (window.clearTimeout || null);
    if (!schedule) return;
    each(scope.querySelectorAll('form.business-form, [data-bblab-business] form'), function (form) {
      if (isInitialised(form)) return;
      bindStorefrontCaptcha(form);
      form.addEventListener('submit', function () {
        if (form.__bblabSubmitTimer && cancel) cancel(form.__bblabSubmitTimer);
        var delay = parseInt(form.getAttribute('data-submit-watchdog-ms'), 10);
        if (!(delay >= 0)) delay = SUBMIT_WATCHDOG_MS;
        var startedAt = Date.now();
        var check = function () {
          // 挑战还在进行中就先不报错，最多等 CAPTCHA_PENDING_MAX_MS。
          if (captchaPending(form) && Date.now() - startedAt < CAPTCHA_PENDING_MAX_MS) {
            form.__bblabSubmitTimer = schedule(check, delay);
            return;
          }
          form.__bblabSubmitTimer = null;
          showSubmitFailure(form);
        };
        form.__bblabSubmitTimer = schedule(check, delay);
      });
      if (window.addEventListener) {
        window.addEventListener('beforeunload', function () {
          if (form.__bblabSubmitTimer) clearTimeout(form.__bblabSubmitTimer);
        });
      }
      markInitialised(form);
    });
  }

  function init(scope) {
    scope = scope || document;
    initStoryTimelines(scope);
    initBusinessTabs(scope);
    initSearch(scope);
    initSubmitWatchdog(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // Theme editor: initialise only the freshly loaded section, idempotently.
  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) init(event.target);
  });
})();
